<script setup lang="ts">
import {
  buildOpenInBrowserUrl,
  readOfficialInviteCode,
  resolvePublicInvite,
  type OfficialPromotionState,
} from './official-promotion'
import { canonicalizeInviteCode, isCustomInviteCode, normalizeInviteCode } from './promotion-config'
import honeypotIcon from './assets/honeypot-app-icon.png'
// App 真实品牌标（installer_desktop_brand_mark.png 缩到 128px 高），页头 / 页脚 / 手机样机共用。
import brandMark from './assets/brand-mark.png'
// 图标随产物内联（原 @iconify/vue 运行时从 api.iconify.design 拉取，国内与内置浏览器常拉不到）。
import { Icon } from './components/Icon'
// 桌面端扫码区的二维码（本地生成，按需加载编码库）。
import { QrCode } from './components/QrCode'
// 顶栏账号入口（官网单点登录）：未登录「登录」、已登录头像 + 下拉；开关关闭时不显示。
import AccountEntry from './components/AccountEntry.vue'
// 页头滚动态、导航高亮、区块淡入（纯展示，不碰拉起 / 下载流程）。
import { enhancePage } from './page-enhance'
// 首屏「下载信息」的版本号 / 大小 / 更新时间（只读接口，失败静默）。
import { fetchDownloadInfo, type DownloadInfo } from './download-info'
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue'

// 拉起 / 下载 / 剪贴板 / 未安装检测 —— 官网不再自己实现，与对外 SDK 共用同一份。
// 这样测试测官网 = 测 SDK，不会再出现「官网修了、SDK 没跟」（#40 即栽在这里）。
//
// 【必须以 script 标签引入，不能 import 内联】共用实现按「加载它的那个 script 的
// 地址」推导接口基址（resolveSelfOrigin）。内联进打包产物后页面里没有本文件的
// script 标签，推导为空会直接报 config-error —— 这是刻意保留的失败，不给"猜一个
// 地址"的空间。所以 index.html 用 <script src="/sdk/honeypot-launch.js"> 引同一份
// 产物，这里只从全局取；类型由 honeypot-launch.d.ts（含 Window 全局声明）提供。
const HoneypotLaunch = window.HoneypotLaunch

/**
 * 「本机未检测到蜜罐」信号。
 *
 * Chromium 路径下这个结论是**浏览器带着标记重新加载本页**之后才知道的，SDK 在
 * setup() 里消费该标记 —— 所以必须在页面加载时就注册，等到点击前一刻就错过了。
 * 但此刻应用信息还没解析完、弹窗也不在 DOM 里，只能先记下来，由 initialize()
 * 在 ready 之后补弹。
 */
let pendingNotInstalled: (() => void) | null = null

/**
 * 弹窗是否已经在 DOM 里（= initialize() 已把 state 置为 ready 并渲染完）。
 *
 * onNotInstalled 有两条触发路径，处置方式不同：
 *   · 页面加载时带回落标记（真 Chrome 判定未装后跳回本页）→ 此刻应用信息还没解析、
 *     弹窗不在 DOM，只能记下来由 initialize() 补弹；
 *   · 用户点击后看门狗超时（国产内核既不拉起也不跳 fallback）→ 页面早已渲染完，
 *     必须**当场**弹窗并下载。
 *
 * 只判断这个布尔量而不去读 state / templateRef，是因为 setup() 会在模块求值期
 * 同步消费回落标记 —— 那时下面那些 const 还在 TDZ 里，碰一下就是 ReferenceError。
 */
let launchDialogReady = false

/**
 * 旧版回落标记（`launchFallback=1`）是否还等着被消费。
 *
 * 【为什么是模块级状态而不是函数参数】它必须活过「解析失败 → 点再试一次 → 成功」
 * 这条路。此前作为 settleAfterReady 的入参传入，首次解析失败时既没被消费、
 * 也没被留住，retryResolve 再调用时传的是默认 false —— 标记就此丢失，
 * 用户点的是部署前的旧链接、又恰好第一次解析失败的话，重试成功后不会自动弹
 * 「未检测到蜜罐」，而这条路径正是本 PR 明确声明要兼容的（复审 #50 阻塞）。
 *
 * 只有真正弹过窗才清零，保证它在中间任何一次失败里都不会被吃掉。
 */
let pendingLegacyFallback = false

/**
 * 弹「未检测到蜜罐」并开始下载。只弹窗不下载，就成了「提示说在下载、实际什么都没发生」。
 *
 * 【下载走官网自己的隐藏 iframe，不用共用实现的下载层】(复审 #50 阻塞)
 *
 * 官网这个弹窗是 <dialog>.showModal()，处在 top layer；共用实现的进度层是
 * document.body 上的普通元素，z-index 再大也压不过 top layer。两者同时出现时
 * 「保存安装包 / 重试」会被压在下面，自动保存被拦或 XHR 失败就没有逃生门了。
 *
 * 换成 progressModal:false 也不行 —— 那条路径直接 location.assign，会把当前页
 * 替换掉，本弹窗（以及它的手动下载按钮）随之消失。官网当初用 iframe 正是为此。
 *
 * 所以这里保持官网原有做法：弹窗负责说明与手动重试，iframe 负责取包。
 * 合作方页面没有这个弹窗，不受影响，照常拿到共用实现的进度层与保存按钮。
 */
/**
 * 未安装弹窗：**只开弹窗，不自动取包**。所有来源共用：
 *
 *   · `timeout` —— 看门狗到点、或浏览器跟随了回落标记（`intent-fallback`）。
 *     两者都**不是可靠的「未安装」裁决**：
 *       - 超时：系统「打开方式」确认框 pending 时不派发任何事件（vivo 真机连 blur
 *         都没有），用户点慢一点，已装的人也会超时；
 *       - 回落：浏览器裁决也会错（#57 红米 K70+Chrome：App 已被探拉打开，Chrome
 *         仍跟随了 `browser_fallback_url`）。
 *     所以只说中性文案「若未唤起蜜罐，请点击此处下载」，**绝不自动取包** ——
 *     否则一次误判就变成一次上百 MB 的真下载。
 *   · `legacy` —— 旧版 `launchFallback=1` 参数的过渡路径（复审 #50 阻塞）。
 *     它是纯 URL 布尔值、补不了一次性凭据，无法校验真伪，同样要点一下才下载。
 *
 * 【弹窗已开着就不再触发】(#44 的修复)：共用实现的 open() 每次调用都新建一轮
 * 看门狗、不取消上一轮，用户 3 秒内连点两次会有两轮都到点，各调一次本函数。
 */
function showNotInstalledManual(variant: 'timeout' | 'legacy' = 'legacy'): void {
  if (downloadDialog.value?.open) return
  downloadDialogVariant.value = variant
  downloadDialog.value?.showModal()
}

HoneypotLaunch.setup({
  // 【下载地址不再显式传】官网用 <script src="/sdk/honeypot-launch.js"> 引入，
  // 共用实现按该 script 的地址推出同源 /api —— 与官网后端同源，就是官网自己的域名。
  // 这同时也是对外 SDK 的形态：谁分发这个 js 就连谁的 /api，没有环境开关。
  // 【官网关掉共用实现自带的下载进度层】(复审 #50 阻塞)
  //
  // 官网自己的「未检测到蜜罐」弹窗用 <dialog>.showModal() 进入 top layer，
  // 而进度层是 document.body 上的普通元素 —— z-index 再大也压不过 top layer。
  // 两者同时出现时，用户看不到「保存安装包 / 重试」，自动保存被拦或 XHR 失败
  // 就彻底没有逃生门，回落下载这条路直接卡死。
  //
  // 官网这边的下载引导与手动重试由自家弹窗全权负责，不需要第二个层；
  // 合作方页面没有这个弹窗，不传该项即可照常拿到进度层与保存按钮。
  progressModal: false,
  // 【关掉共用实现自带的受限浏览器遮罩】官网有自己那套 restrictedDialog：
  // 带应用图标、名称、复制链接按钮，以及 QQ 没有「···」时的特殊文案，信息更全。
  // 两套同时出现会叠成两层（SDK 那层是普通 z-index，官网的是 <dialog> top layer，
  // 官网盖在上面、SDK 那层在底下露边）。
  //
  // ⚠️ 这【不影响微信原生弹窗】：原生「用其他应用打开」由共用实现请求
  // /api/v1/public/official/open 中转触发，与本开关无关，关掉的只是那层黑底提示。
  restrictedGuide: false,
  // 复制回执：共用实现在点击瞬间同步写剪贴板，成功与否只通过事件回传。
  // 不接这个事件，用户点完「打开蜜罐」就完全没有可见反馈，无从确认邀请码
  // 是否已进剪贴板 —— 而未安装的用户正是靠剪贴板把邀请关系带进蜜罐的（复审 #50 建议项）。
  onEvent: (stage: string) => {
    if (stage === 'clipboard-copied') void showToast('邀请码已复制')
  },
  // 【接管 iOS 提示，避免叠两层】共用实现在没有本回调时会画自带的「仅支持安卓」
  // 引导层（零配置接入的第三方靠它兜底，否则点下去毫无反应）。官网有自己那套
  // 带应用图标、名称、复制链接按钮的 unsupportedPlatform 遮罩，信息更全，
  // 所以这里注册回调把 SDK 那层顶掉 —— 与 restrictedGuide:false 是同一个道理。
  onUnsupportedPlatform: () => {
    unsupportedPlatform.value = true
  },
  onNotInstalled: () => {
    // 两种 via **都不再自动下载**，也不再说「未检测到」：
    //   · launch-timeout —— 超时只代表「到点了页面还在」，vivo 系统框 pending 时会误判；
    //   · intent-fallback —— 浏览器裁决也会错：#57 红米 K70+Chrome 实测，App 已被
    //     iframe 探拉打开，Chrome 对随后那次多余的 intent 仍跟随了 browser_fallback_url
    //     重载本页 —— 这时自动下载就是白下上百 MB。
    // 统一弹中性手动窗，点了才下载。真正没装的用户也只多一次点击。
    const show = () => showNotInstalledManual('timeout')
    if (launchDialogReady) {
      show()
      return
    }
    pendingNotInstalled = show
  },
  onLaunchHandoff: () => {
    // 看门狗超时后页面才交接出去(=上一次判定打早了):撤回误弹的弹窗,
    // 停掉已开始的 iframe 取包。还没开始(手动路径)时这两步都是 no-op。
    // pending 一起清掉:否则回到前台后的 settleAfterReady 会把存下的弹窗补出来。
    pendingNotInstalled = null
    downloadFrame.value?.removeAttribute('src')
    downloadDialog.value?.close()
  },
})

const hasInviteCode = new URLSearchParams(window.location.search).has('code')
// 自定义邀请码（需求 5006346）转小写、标准码转大写；都不是时沿用原来的转大写。
const rawQueryInviteCode = new URLSearchParams(window.location.search).get('code') || ''
const preservedInviteCode = ref(canonicalizeInviteCode(rawQueryInviteCode) || normalizeInviteCode(rawQueryInviteCode))

document.title = hasInviteCode ? '正在打开好友分享 - 蜜罐' : '蜜罐 - 轻松安装好友分享的应用'
document.documentElement.dataset.promotionSite = 'official'

const state = ref<OfficialPromotionState>(hasInviteCode
  ? { kind: 'loading', inviteCode: '' }
  : { kind: 'home' })
const imageFailed = ref(false)
const toastMessage = ref('')
const toastVisible = ref(false)
const restrictedDialog = useTemplateRef<HTMLDialogElement>('restrictedDialog')
const downloadDialog = useTemplateRef<HTMLDialogElement>('downloadDialog')
const downloadFrame = useTemplateRef<HTMLIFrameElement>('downloadFrame')
const inviteCodeElement = useTemplateRef<HTMLElement>('inviteCodeElement')
const dialogInviteCodeElement = useTemplateRef<HTMLElement>('dialogInviteCodeElement')
const toastElement = useTemplateRef<HTMLElement>('toastElement')
const restrictedBrowser = ref(false)
const restrictedBrowserName = ref<'微信' | 'QQ' | '应用内浏览器'>('应用内浏览器')
const canTryExternalBrowser = ref(false)
const copiedPage = ref(false)
const unsupportedPlatform = ref(false)
/**
 * 弹窗措辞档位，决定标题 / 正文 / 主按钮三处文案（见 showNotInstalledManual）：
 *   · `timeout` —— 默认。看门狗超时或浏览器跟随回落（`intent-fallback`）都走它：
 *     两者都可能误判（#57），所以只说中性文案「若未唤起蜜罐，请点击此处下载」、
 *     不自动取包；
 *   · `legacy`  —— 旧版 `launchFallback=1` 过渡路径：措辞保持原样，同样不自动取包
 *     （复审 #50 阻塞）。
 */
type DownloadDialogVariant = 'timeout' | 'legacy'
const downloadDialogVariant = ref<DownloadDialogVariant>('timeout')
/** iOS 且身处微信/QQ 内置浏览器——此时才值得提示「右上角 ···」,Safari 里没这个菜单。 */
const unsupportedInRestrictedBrowser = ref(false)

const unsupportedPlatformAppName = computed(() => {
  return state.value.kind === 'ready' ? state.value.app.appName : '蜜罐'
})

/**
 * 「未检测到应用」弹窗里的应用名，形如 `「好迷TV」`。
 * 应用信息还没解析出来时留空，文案退化为不带书名号的通用说法，仍然通顺。
 */
const launchFallbackAppLabel = computed(() => {
  return state.value.kind === 'ready' && state.value.app.appName ? `「${state.value.app.appName}」` : ''
})

/**
 * 失败态标题。
 *
 * `malformed` 与 `invalid` 必须分开说（#32）：
 * - `malformed` = 本地格式校验没过，这个码**从来没有效过**（用户手打错、链接被截断）；
 * - `invalid` = 后端 501009，码后端不认了，「不能用了」隐含它曾经有效。
 * 都说「不能用了」，会让链接本身就是错的用户以为是我们把它作废了。
 */
const failureTitle = computed(() => {
  switch (state.value.kind) {
    case 'malformed':
      return '这个链接不是有效的邀请链接'
    case 'invalid':
      return '这个链接不能用了'
    case 'expired':
      return '这个分享已过期'
    case 'unavailable':
      return '这个应用暂时无法获取'
    case 'rejected':
      return state.value.message
    default:
      return '页面暂时打不开'
  }
})

/** 失败态副文案：告诉用户下一步能做什么，而不只是重复一遍失败。 */
const failureLead = computed(() => {
  switch (state.value.kind) {
    case 'malformed':
      return '邀请码格式不正确，请向好友重新索取完整的分享链接。'
    case 'error':
      return '应用信息暂时没加载出来。已安装蜜罐可直接打开，也可以检查网络后再试。'
    case 'rejected':
      return '请向好友确认邀请码，或重新索取分享链接。'
    default:
      return '你可以先下载蜜罐，稍后再打开好友分享的链接。'
  }
})

/**
 * 被分享应用的图标，取不到（未解析出 / 没配图 / 加载失败）时回退蜜罐通用图标。
 * iOS 不支持遮罩、微信QQ 遮罩、未检测到应用弹窗三处共用——这几个界面讲的都是
 * 「你要装的那个应用」，用它自己的图标才对得上用户预期。
 */
const sharedAppIcon = computed(() => {
  if (state.value.kind === 'ready' && state.value.app.icon && !imageFailed.value) {
    return state.value.app.icon
  }
  return honeypotIcon
})

const restrictedBrowserAppName = computed(() => {
  return state.value.kind === 'ready' ? state.value.app.appName : '蜜罐'
})

/** 记住用户已选择「仍然访问官网」,之后不再拦。 */
const IOS_GATE_DISMISSED_KEY = 'honeypot:official:ios-gate-dismissed'

/**
 * localStorage 一律 try/catch:Safari 无痕模式下 getItem/setItem 都会抛异常,
 * 不兜住会让整个 onMounted 挂掉、官网直接白屏。读失败按「没记过」处理(照常提示),
 * 写失败也只是下次再提示一遍,都不影响本次浏览。
 */
function readIosGateDismissed(): boolean {
  try {
    return window.localStorage.getItem(IOS_GATE_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * 关闭「不支持」遮罩,继续浏览官网,并记住这个选择。
 *
 * 【只在普通浏览器里提供】微信/QQ 内的首要任务是先把用户引出去(复制链接或换浏览器),
 * 给「仍然访问」会让人留在内置浏览器里,后续下载体验依旧受限;而用户已经在 Safari 等
 * 正常浏览器里时,是否继续看应由他自己决定——毕竟浏览内容、复制邀请码都还有意义。
 *
 * 【为什么要记住】这是一次明确的知情选择("我知道装不了,我就想看")。每次进来都拦一遍
 * 等于把用户已经回答过的问题反复再问,尤其邀请链接常被反复点开。
 */
function dismissUnsupportedPlatform() {
  unsupportedPlatform.value = false
  try {
    window.localStorage.setItem(IOS_GATE_DISMISSED_KEY, '1')
  } catch {
    // 存不下就下次再提示一遍,不影响本次浏览
  }
}

const inviteCode = computed(() => ('inviteCode' in state.value ? state.value.inviteCode : ''))
/**
 * 当前可以直接拉起蜜罐的邀请码：ready（已解析出应用）或 error（后端暂时没解析成功，
 * 但码已过本地校验）。invalid / expired / unavailable 是后端的明确裁决，拉起后
 * 蜜罐同样会拒绝，不给入口。
 */
const openableInviteCode = computed(() => {
  const current = state.value
  return current.kind === 'ready' || current.kind === 'error' ? current.inviteCode : ''
})
const isInvitePage = computed(() => state.value.kind !== 'home')

/**
 * 当前是不是自定义邀请码（需求 5006346）。ready 态以后端返回的 inviteType 为准，
 * error 态（后端没解析出来）按本地格式判断。
 */
const isCustomInvite = computed(() => {
  const current = state.value
  if (current.kind === 'ready') return current.inviteType === 'CUSTOM'
  return current.kind === 'error' && isCustomInviteCode(current.inviteCode)
})

/**
 * 自定义模式下的分享文案（PRD §10.1）。标准模式不展示，沿用现有页面不改写。
 */
const customShareText = computed(() => {
  const current = state.value
  if (current.kind !== 'ready' || current.inviteType !== 'CUSTOM') return ''
  return `推荐你试试「${current.app.appName}」，打开蜜罐即可安装。邀请码：${current.inviteCode}`
})

const customHintInviteCodeElement = useTemplateRef<HTMLElement>('customHintInviteCodeElement')

/**
 * 老客户端识别不了自定义邀请码（PRD §9.4.6）：官网拿不到用户本机蜜罐的版本，
 * 所以只要是自定义码就常显一行「手动复制 + 升级」提示，不去猜版本。
 */
async function handleCopyCustomHintInvite(): Promise<void> {
  try {
    await copyInviteCode()
    await showToast('邀请码已复制')
  } catch {
    selectInviteCode(customHintInviteCodeElement.value)
    await showToast('已选中邀请码，请手动复制')
  }
}

/**
 * 苹果移动设备(iPhone / iPod / iPad)检测。蜜罐只有 Android 版，这些设备装不了。
 *
 * 【为什么要单独判 Macintosh】iPadOS 13 起默认勾选「桌面版网站」，UA 里不再出现 iPad、
 * 而是伪装成 Macintosh，仅靠 /iPad/ 会漏掉绝大多数 iPad。真 Mac 没有触摸屏
 * (maxTouchPoints 恒为 0)，因此用触控点数把两者区分开。
 *
 * 【阈值为什么是 > 0 而不是常见的 > 1】真机 iPad 报 5，两种写法都能命中；但 > 1 会漏掉
 * 只报 1 个触控点的环境(playwright 模拟的移动上下文就是 1，实测因此漏判 iPad)。
 * 既然真 Mac 恒为 0，放宽到 > 0 不会误伤，却能覆盖这类边界，也才可验证。
 *
 * 【为什么不把真 Mac 也算进来】Mac 用户下载 APK 再转给安卓手机是正当用途，
 * 对着桌面浏览器说「不支持您的系统」反而莫名其妙。这里只拦装不了的移动设备。
 *
 * maxTouchPoints 作为入参而非直接读 navigator，是为了能被测试覆盖。
 */
function detectApplePlatform(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPod|iPad/i.test(userAgent)) return true
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 0
}

function detectRestrictedBrowser(userAgent: string): '微信' | 'QQ' | '应用内浏览器' | null {
  if (/MicroMessenger/i.test(userAgent)) return '微信'
  if (/\bQQ\//i.test(userAgent) || /\bTIM\//i.test(userAgent)) return 'QQ'
  return null
}

function tryOpenExternalBrowser(): void {
  if (!canTryExternalBrowser.value) {
    void showToast('请点击右上角 ···，选择「在浏览器中打开」')
    return
  }

  // 微信内置浏览器会拦截 intent:// / 自定义 scheme，纯前端唤不起外部浏览器。
  // 改为跳到 App 层公开中转接口：微信 UA 会收到 attachment 响应，从而弹出原生「用浏览器打开」。
  window.location.href = buildOpenInBrowserUrl(preservedInviteCode.value || inviteCode.value)
  window.setTimeout(() => {
    void showToast('若按钮未成功唤起弹窗，请从右上角 ···，选择「在浏览器中打开」')
  }, 800)
}

function tryAutoOpenExternalBrowser(): void {
  if (!canTryExternalBrowser.value) return
  window.setTimeout(() => {
    tryOpenExternalBrowser()
  }, 80)
}

/** toast 停留时长，到点自动淡出。 */
const ToastDurationMs = 2400
/** 与 .official-toast 的 opacity/transform 过渡时长一致，淡出结束再退出 top layer。 */
const ToastFadeMs = 180

let toastHideTimer = 0
let toastPopoverTimer = 0

/**
 * 让 toast 进入 top layer，确保它能盖在 `<dialog showModal()>` 之上。
 *
 * 【为什么需要】modal dialog 处于 top layer，普通元素无论 z-index 多大都被压在下面。
 * 实测在「未检测到应用」弹窗里点「复制邀请码」，toast 的 elementFromPoint 命中的是
 * 弹窗本身——提示确实渲染了，但用户完全看不到。
 *
 * 【为什么每次都先 hide 再 show】top layer 按进入顺序叠放，后进入的在上层。toast 若
 * 一直驻留，dialog 后打开就会盖住它；每次重新进入才能保证它排在最后。
 *
 * 【为什么要 feature detect】popover 需要 Chrome 114+/Safari 17+。老设备上退化为普通
 * 定位元素——页面级 toast 照常可见，仅弹窗内那一次可能被遮挡，不影响功能。
 */
function raiseToastToTopLayer(el: HTMLElement | null): void {
  if (!el || typeof el.showPopover !== 'function') return
  try {
    if (el.matches(':popover-open')) el.hidePopover()
    el.showPopover()
  } catch {
    // 不支持或状态异常时静默退化为普通定位元素
  }
}

function dismissToastFromTopLayer(el: HTMLElement | null): void {
  if (!el || typeof el.hidePopover !== 'function') return
  try {
    if (el.matches(':popover-open')) el.hidePopover()
  } catch {
    // 同上
  }
}

/**
 * 弹一条一过性提示。
 *
 * 🔴 原实现只把 toastVisible 置 true，**没有任何地方置回 false**，提示弹出来就永久
 * 挂在屏幕上（页面上的「复制邀请码」按钮一直是这个表现）。这里补上自动淡出。
 */
async function showToast(message: string): Promise<void> {
  window.clearTimeout(toastHideTimer)
  window.clearTimeout(toastPopoverTimer)
  toastMessage.value = message
  toastVisible.value = false
  await nextTick()
  raiseToastToTopLayer(toastElement.value)
  // 强制回流，让下面这次 class 变更能触发过渡动画（popover 刚从 display:none 出来同理）
  void toastElement.value?.offsetWidth
  toastVisible.value = true

  toastHideTimer = window.setTimeout(() => {
    toastVisible.value = false
    // 等淡出动画走完再退出 top layer，否则会瞬间消失没有过渡
    toastPopoverTimer = window.setTimeout(() => dismissToastFromTopLayer(toastElement.value), ToastFadeMs)
  }, ToastDurationMs)
}

/**
 * 写剪贴板。实现在共用模块里（与对外 SDK 同一份）。
 *
 * 官网原先自己实现过一份：`execCommand('copy')` 返回 true 只代表命令被派发，
 * 不代表真写进了剪贴板（#34：页面提示「已复制」而剪贴板是空的）。那个坑修在
 * 两份代码里就要修两次，漏一次就复发 —— 所以这里只保留「抛错以便 UI 走降级」
 * 这一层语义转换，判定本身不再自己写。
 */
async function writeClipboard(value: string): Promise<void> {
  const ok = await HoneypotLaunch.copyText(value)
  if (!ok) throw new Error('copy unavailable')
}

async function copyInviteCode(): Promise<void> {
  await writeClipboard(inviteCode.value)
}

async function copyCurrentPage(): Promise<void> {
  try {
    await writeClipboard(window.location.href)
    copiedPage.value = true
    await showToast('链接已复制，请在外部浏览器中打开')
  } catch {
    await showToast('复制失败，请长按地址栏复制链接')
  }
}

function selectInviteCode(element: HTMLElement | null): void {
  if (!element) return
  const range = document.createRange()
  range.selectNodeContents(element)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

async function handleCopyInvite(): Promise<void> {
  try {
    await copyInviteCode()
    await showToast('邀请码已复制')
  } catch {
    selectInviteCode(inviteCodeElement.value)
    await showToast('已选中邀请码，请手动复制')
  }
}

/**
 * 当前浏览器下**实际**会用的拉起地址：Chromium 走 intent://，其余走裸 scheme。
 *
 * 必须与 SDK 点击时真正发出的地址逐字一致（含回落标记），否则这个属性
 * 会把测试和排障往错误方向带 —— 所以 fallback 地址也取 SDK 自己那份。
 */
function launchUrlFor(code: string): string {
  return HoneypotLaunch.supportsIntentUrl()
    ? HoneypotLaunch.buildIntentUrl(code, HoneypotLaunch.buildFallbackReturnUrl(code))
    : HoneypotLaunch.buildSchemeUrl(code)
}

/** 供模板绑定 data-invite-scheme 用。 */
function inviteSchemeFor(code: string): string {
  return HoneypotLaunch.buildSchemeUrl(code)
}

/**
 * 「打开蜜罐」——已装拉起，未装弹提示并自动下载。
 *
 * 【为什么点击时就复制邀请码】未装的用户会被引导去下载，装完是**从桌面图标**启动
 * 蜜罐的，不会再回到浏览器走一次拉起链接——邀请码在那一刻就丢了。蜜罐启动时会读
 * 剪贴板并提示「检测到邀请码」，靠这条通道把关系接上。而且 Chromium 路径下「没装
 * → 跳回本页」是**浏览器**做的，页面脚本没有第二次执行机会；剪贴板写入又必须发生
 * 在用户手势里。所以只能在点击瞬间无条件先写，已装的用户多复制一次无副作用。
 *
 * 【两条路径】
 *  · Chromium：intent:// 交给系统判定，没装由浏览器带 launchFallback 标记跳回本页；
 *  · 其它浏览器（火狐等不认 intent://）：裸 scheme + 计时兜底，超时未离开即视为未装。
 */
async function handleOpenHoneypot(): Promise<void> {
  // ready 与 error 两态都能拉起（#57 yanzhe 9-21 复测）：error 只代表「官网后端
  // 没把应用信息解析出来」（网关 /api 未转发、503、网络抖动），邀请码本身已过本地
  // 校验位，蜜罐 App 自己会再解析一次 —— 拉起蜜罐根本不依赖这次解析。
  // 原先只有 ready 态有「打开蜜罐」，解析一失败，已装蜜罐的用户在整页只能看到
  // 「下载蜜罐」，正是「本地有蜜罐、访问官网还是下载」的现场。
  const code = openableInviteCode.value
  if (!code) return

  // 全部交给 SDK：内核判定、intent/scheme 拉起、看门狗、剪贴板、未安装兜底。
  // 官网只负责「弹什么窗、显示什么文案」这一层。
  const result = await HoneypotLaunch.open(code)
  if (result === 'restricted') {
    restrictedDialog.value?.showModal()
    return
  }
  // 与 handleDirectDownload 同一处置：iOS 只有安卓包可下。
  // 用户点掉过「不支持您的系统」遮罩（或此前选过「仍然访问官网」）之后，
  // 邀请页仍会展示「打开蜜罐」主按钮；不处理 unsupported-ios 的话点了完全没反馈，
  // 用户既不知道只支持安卓，也不知道下一步该把链接复制到安卓设备（复审 #50 阻塞）。
  //
  // ⚠️ 这两个入口必须一起改：上一轮只修了 handleDirectDownload，漏了这里。
  if (result === 'unsupported-ios') unsupportedPlatform.value = true
}

/**
 * 未检测到蜜罐：弹提示并用隐藏 iframe 开始下载。
 *
 * 用 iframe 而不是跳转，是为了**不把当前页面换掉**——弹窗要一直留在用户眼前，
 * 告诉他自动下载失败时可以走页头/页尾的下载按钮。
 */
function showLaunchFallback(): void {
  downloadDialog.value?.showModal()
  void HoneypotLaunch.download()
}

function handleDirectDownload(): void {
  void HoneypotLaunch.download().then(result => {
    if (result === 'restricted') {
      restrictedDialog.value?.showModal()
      return
    }
    // iOS 只有安卓包可下，共用实现返回 unsupported-ios。
    // 不处理的话按钮表面可点、实际什么都不发生 —— 用户会以为按钮坏了（复审 #50 阻塞）。
    // 用户此前可能点掉过「不支持您的系统」遮罩，这里把它重新弹出来。
    if (result === 'unsupported-ios') unsupportedPlatform.value = true
  })
}

async function retryCopyInvite(): Promise<void> {
  try {
    await copyInviteCode()
    await showToast('邀请码已复制')
  } catch {
    selectInviteCode(dialogInviteCodeElement.value)
    await showToast('已选中邀请码，请手动复制')
  }
}

function closeDialog(dialog: HTMLDialogElement | null): void {
  dialog?.close()
}

async function initialize(): Promise<void> {
  const query = await readOfficialInviteCode()
  preservedInviteCode.value = query.code
  if (!query.code) {
    state.value = { kind: 'home' }
    return
  }
  if (!query.valid) {
    // 本地格式校验没过 = 这个码从没有效过,不能说「不能用了」(#32)
    state.value = { kind: 'malformed' }
    return
  }
  state.value = { kind: 'loading', inviteCode: query.code }
  state.value = await resolvePublicInvite(query.code)
  if (state.value.kind === 'ready') {
    document.title = `${state.value.app.appName} - 蜜罐`
  }
  // 「浏览器判定没装」这一路由 SDK 通知（见文件顶部 setup 的 onNotInstalled）。
  // 解析完成后若已收到该信号，此刻才补弹窗——弹窗文案里的应用名才有内容。
  //
  // 🔴 必须先 await nextTick：loading 态下整个 <template v-else> 分支（含本弹窗）
  // 都不在 DOM 里，templateRef 还是 null。刚把 state 置为 ready 就调用，会被
  // 可选链静默吞掉——弹不出窗、也不报任何错。
  pendingLegacyFallback = query.legacyLaunchFallback
  await settleAfterReady()
}

/**
 * state 落定并渲染完之后的收尾。**首次解析与重试都必须走这里**。
 *
 * launchDialogReady 决定 onNotInstalled 是「当场弹窗」还是「先存起来等消费」。
 * 之前只有 initialize() 会把它置 true，retryResolve() 只更新 state 就完事 ——
 * 于是「首次解析失败 → 点再试一次 → 成功 → 未安装机型点打开蜜罐」这条路上，
 * 看门狗超时后回调只把 pendingNotInstalled 存下来，而已经没有新的 initialize()
 * 来消费它，弹窗和下载都不会出现，用户又回到「点了没反应」（复审 #50 阻塞）。
 */
async function settleAfterReady(): Promise<void> {
  await nextTick()
  // 【门槛是「弹窗在不在 DOM 里」，不是「解析成没成功」】(复审 #50 阻塞)
  //
  // 之前写的是 kind === 'ready'。可是回落这一路的时序是：用户点「打开蜜罐」→
  // 浏览器判定未安装 → 带 hpLaunchFallback=1 重载官网 → 【这一次重载要重新解析】。
  // 只要这次解析没成（503、网络抖动、或邀请码刚好过期这类终态），页面停在 error，
  // 回落信号就永远没人消费 —— 弹窗不弹、iframe 不取包，用户点了半天只剩自己回首页。
  // 终态错误更糟：连「再试一次」都救不回来，这条下载路彻底断死。
  //
  // 而弹窗本身根本不需要解析结果：它整个在 v-else 分支里（非 loading 就已渲染），
  // 文案主语是蜜罐，应用名走 launchFallbackAppLabel —— 非 ready 时自动退成通用文案。
  // 所以只要不是 loading（弹窗 DOM 已在），就该把回落消费掉。
  launchDialogReady = state.value.kind !== 'loading'
  // 新链路的「未安装」信号由共用实现经 onNotInstalled 送达；
  // 旧链路（部署前已打开的页面生成的 launchFallback=1）在这里兼容一次。
  //
  // 仍在 loading 时【什么都不清】：两个标记都留到渲染完再消费。
  if (!launchDialogReady) return
  // 新链路（共用实现送来的信号，已过一次性凭据校验）：按存下时的那条路消费——
  // 回落标记走自动取包，看门狗超时走手动(只弹窗、下载等用户点一下)。
  // 旧参数（纯 URL 布尔值，补不了凭据）：只开弹窗，要用户点一下才下载。
  if (pendingNotInstalled) {
    const show = pendingNotInstalled
    pendingNotInstalled = null
    pendingLegacyFallback = false
    show()
  } else if (pendingLegacyFallback) {
    pendingLegacyFallback = false
    showNotInstalledManual()
  }
}

function retryResolve(): void {
  const code = inviteCode.value
  if (!code) return
  state.value = { kind: 'loading', inviteCode: code }
  void resolvePublicInvite(code).then(async nextState => {
    state.value = nextState
    // 与首次解析同一条收尾：不走这里，重试成功后未安装兜底会被永久挂起。
    await settleAfterReady()
  })
}

// 正文（页头、各区块）在 loading 态下不渲染，等它进了 DOM 再挂展示增强；enhancePage 自身幂等。
// immediate 的首次回调在 setup 期间同步执行，此时还没挂载，所以统一等到下一个 tick
watch(() => state.value.kind !== 'loading', ready => {
  if (ready) void nextTick(enhancePage)
}, { immediate: true })

/**
 * 当前安装包信息（版本号 / 大小 / 更新时间），页面加载后请求一次。
 * null = 还没回来或拿不到：此时只显示原有静态信息，不出占位、不报错。
 */
const downloadInfo = ref<DownloadInfo | null>(null)

/**
 * 首屏「下载信息」行（v5 设计稿：`版本 1.2.2 · 120.6 MB · Android 7.0 及以上`）里的动态项：
 * 只列出接口确实给了的那几项，排在静态的系统要求之前。更新时间只放在收尾横幅与弹窗里。
 */
const downloadInfoFacts = computed(() => {
  const info = downloadInfo.value
  if (!info) return []
  const facts: Array<{ key: string; text: string }> = []
  if (info.version) facts.push({ key: 'version', text: `版本 ${info.version}` })
  if (info.size) facts.push({ key: 'size', text: info.size })
  return facts
})

/**
 * 首屏顶部小标签里的版本号（「1.2.2 新版支持自定义邀请码」）。拿不到就退成「新版 支持自定义邀请码」，
 * 不补默认版本号。
 */
const heroBadgeVersion = computed(() => (downloadInfo.value && downloadInfo.value.version) || '')

/**
 * 收尾横幅里的一行摘要，如「版本 1.1.38 · 102.2 MB · 更新于 2026-10-28」。
 * 拆成片段逐个渲染（每段不折行）：窄屏放不下一行时只在「·」处换行，
 * 不会把「更新于」这类词从中间断开。
 */
const downloadInfoSummaryParts = computed(() => {
  const info = downloadInfo.value
  if (!info) return []
  const parts: string[] = []
  if (info.version) parts.push(`版本 ${info.version}`)
  if (info.size) parts.push(info.size)
  if (info.date) parts.push(`更新于 ${info.date}`)
  return parts
})

/** 「产品能力」六宫格（v5 设计稿 renderVals().features）。图标是设计稿自绘的 hp-feature-*。 */
const features = [
  { title: '独立空间运行', body: '每个应用都运行在与系统隔离的独立空间，移除后数据一并清除。', icon: 'hp-feature-space' },
  { title: '安装前安全检测', body: '校验来源与完整性，检测通过后才会放进你的空间。', icon: 'hp-feature-shield' },
  { title: '一码直达', body: '支持 13 位标准码，也支持好记的自定义码。', icon: 'hp-feature-code' },
  { title: '剪贴板自动识别', body: '复制好友的分享文案，打开蜜罐就能识别邀请码。', icon: 'hp-feature-clipboard' },
  { title: '账号同步', body: '登录聚点账号，在官网随时查看已安装的应用。', icon: 'hp-feature-sync' },
  { title: '添加到桌面', body: '常用的应用可以放到手机桌面，一键直接打开。', icon: 'hp-feature-desktop' },
]

onMounted(() => {
  // 与页面其余流程完全解耦：不 await、不影响拉起 / 下载，拿到了再补进页面。
  void fetchDownloadInfo().then(info => {
    if (info) downloadInfo.value = info
  })
})

onMounted(async () => {
  // 【iOS 优先于微信/QQ 引导】iPhone 就算跳到外部浏览器,一样装不了 Android 应用,
  // 再弹「请在浏览器中打开」等于把用户带进死路(而且 iOS 微信没有唤起按钮,只剩复制链接)。
  // 所以这里改为直接说明不支持,不再进入 restrictedBrowser 分支。
  //
  // 【为什么不给「继续访问官网」】继续浏览一个装不了的版本对 iOS 用户没有意义,
  // 做成主按钮反而把人往无效路径上推。真正的出路只有两条,故只提供这两条:
  //   ① 复制链接,拿到安卓设备上打开(主按钮);
  //   ② 若身处微信/QQ,可从右上角 ··· 换到外部浏览器(仅此时提示,Safari 无该菜单)。
  const startInitialize = () =>
    initialize().catch(() => {
      state.value = inviteCode.value ? { kind: 'error', inviteCode: inviteCode.value } : { kind: 'home' }
    })

  if (detectApplePlatform(navigator.userAgent, navigator.maxTouchPoints)) {
    // 带邀请码时先解析应用信息,避免 iOS 遮罩先显示通用蜜罐名称和图标。
    await startInitialize()

    // 已选过「仍然访问官网」就不再拦,直接进官网。
    if (!readIosGateDismissed()) {
      unsupportedPlatform.value = true
      const inApp = detectRestrictedBrowser(navigator.userAgent)
      unsupportedInRestrictedBrowser.value = inApp !== null
      if (inApp) restrictedBrowserName.value = inApp
    }
    return
  }

  const browserName = detectRestrictedBrowser(navigator.userAgent)
  if (browserName) {
    if (hasInviteCode) {
      await startInitialize()
    } else {
      state.value = { kind: 'home' }
    }
    restrictedBrowser.value = true
    restrictedBrowserName.value = browserName
    canTryExternalBrowser.value = browserName === '微信' && /Android/i.test(navigator.userAgent)
    tryAutoOpenExternalBrowser()
    return
  }
  startInitialize()
})
</script>


<template>
  <template v-if="hasInviteCode && state.kind === 'loading'">
    <div class="official-initial-loading" role="status" aria-live="polite">
      <div class="official-initial-loading-card">
        <img class="official-initial-loading-logo" :src="brandMark" alt="" width="42" height="48" />
        <p class="official-initial-loading-kicker">正在打开好友分享</p>
        <h1>正在查找应用</h1>
        <p class="official-lead">马上就好，请稍候。</p>
        <div class="official-loading-line"><span></span></div>
      </div>
    </div>
  </template>
  <template v-else>
  <!-- iOS 不支持提示。复用 official-browser-overlay 视觉(fixed 全屏遮罩)。
       主按钮恒为「复制链接」——最终都得拿到安卓设备上装,这是唯一通用出路。
       次要位置按所处环境二选一:
         · 微信/QQ 内 → 右上角箭头 + 「点右上角 ··· 换浏览器」,先把人引出去;
         · 普通浏览器 → 「仍然访问官网」,用户已在正常环境,是否继续看由他自己定。
       箭头同样只在微信/QQ 内画:Safari 没有 ··· 菜单,箭头会指向空处。 -->
  <div v-if="unsupportedPlatform" class="official-browser-overlay" role="dialog" aria-modal="true">
    <div v-if="unsupportedInRestrictedBrowser" class="official-browser-overlay-arrow" aria-hidden="true"><Icon icon="lucide:arrow-up-right" /></div>
    <div class="official-browser-overlay-content">
      <img :src="sharedAppIcon" :alt="`${unsupportedPlatformAppName}图标`" @error="imageFailed = true" />
      <p>{{ unsupportedPlatformAppName }} Android 版</p>
      <h1>不支持您的系统</h1>
      <span>{{unsupportedPlatformAppName}}仅提供 Android 版本，iPhone / iPad 无法安装。<br />请复制链接，在安卓设备中打开下载。</span>
      <button type="button" @click="copyCurrentPage"><Icon icon="lucide:link" /> {{ copiedPage ? '链接已复制' : '复制链接' }}</button>
      <span v-if="unsupportedInRestrictedBrowser && restrictedBrowserName === 'QQ'">或点击右上角 ···，选择「在浏览器中打开」。如果没有三个点，请复制链接并从浏览器打开。</span>
      <span v-else-if="unsupportedInRestrictedBrowser">或点击右上角 ···，选择「在浏览器中打开」</span>
      <button v-else class="official-browser-overlay-copy" type="button" @click="dismissUnsupportedPlatform"><Icon icon="lucide:arrow-right" /> 仍然访问官网</button>
    </div>
  </div>

  <div v-if="restrictedBrowser" class="official-browser-overlay" role="dialog" aria-modal="true">
    <div class="official-browser-overlay-arrow" aria-hidden="true"><Icon icon="lucide:arrow-up-right" /></div>
    <div class="official-browser-overlay-content">
      <img :src="sharedAppIcon" :alt="`${restrictedBrowserAppName}图标`" @error="imageFailed = true" />
      <p>{{ restrictedBrowserAppName }} - {{ restrictedBrowserName }}内置浏览器</p>
      <h1>请在浏览器中打开</h1>
      <span v-if="restrictedBrowserName === 'QQ'">点击右上角 ···，选择「在浏览器中打开」。如果没有三个点，请复制链接并从浏览器打开。</span>
      <span v-else>点击右上角 ···，选择「在浏览器中打开」</span>
      <button v-if="canTryExternalBrowser" type="button" @click="tryOpenExternalBrowser"><Icon icon="lucide:globe" /> 在浏览器中打开</button>
      <button class="official-browser-overlay-copy" type="button" @click="copyCurrentPage"><Icon icon="lucide:link" /> {{ copiedPage ? '链接已复制' : '复制链接备用' }}</button>
    </div>
  </div>

  <!-- 页头（v5）：左侧品牌标 + 导航，右侧账号入口 + 「下载 App」。
       分享页（带邀请码）照设计稿只留品牌、登录与下载，不出锚点导航。 -->
  <header class="official-header" :class="{ 'official-header-invite': isInvitePage }">
    <div class="official-shell official-header-inner">
      <div class="official-header-main">
        <a class="official-brand" href="/" aria-label="返回蜜罐首页">
          <img :src="brandMark" alt="蜜罐" width="23" height="26" />
          <span>蜜罐</span>
        </a>
        <nav v-if="!isInvitePage" class="official-nav" aria-label="页面导航">
          <a href="#features">产品</a>
          <a href="#how-it-works">使用方法</a>
          <a href="#security">安全</a>
          <a href="#faq">帮助中心</a>
        </nav>
      </div>
      <div class="official-header-actions">
        <!-- 官网单点登录入口（设计文档 §5.3）：后端开关关闭 / 接口不存在时不渲染任何内容 -->
        <AccountEntry />
        <button id="header-download" class="official-header-download" type="button" data-action="download-honeypot" @click="handleDirectDownload">下载 App</button>
      </div>
    </div>
  </header>

  <main>
    <section class="official-hero" :class="{ 'official-hero-invite': isInvitePage }">
      <template v-if="state.kind === 'home'">
        <!-- official-hero-grid-with-stage 代替原先样式里的 :has(.official-product-stage)。
             :has() 要 Chromium 105+，老内核解析不了会把【整条规则丢弃】（#38）。
             首页的手机样机是无条件渲染的，所以这个 class 也无条件加；两者的一致性由回归用例守着。 -->
        <div class="official-shell official-hero-grid official-hero-grid-with-stage">
          <div class="official-hero-copy">
            <!-- 顶部小标签：版本号来自 /public/pkg/info，拿不到时只说「新版」，不补默认版本号 -->
            <a class="official-eyebrow" href="#download"><span>{{ heroBadgeVersion || '新版' }}</span>{{ heroBadgeVersion ? '新版支持自定义邀请码' : '支持自定义邀请码' }}<Icon icon="lucide:chevron-right" aria-hidden="true" /></a>
            <!-- 渐变字只在支持 background-clip:text 的内核上生效；不支持时是纯色橙字（见样式里的回退顺序） -->
            <h1>好友分享的应用<br /><span class="official-hero-highlight">一码</span>就能装好</h1>
            <p class="official-lead">输入好友分享的邀请码，蜜罐完成安全检测后，把应用安装进与系统隔离的独立空间。</p>
            <div class="official-actions">
              <button id="home-download" class="official-button official-button-primary official-button-lg" type="button" data-action="download-honeypot" @click="handleDirectDownload">
                <Icon icon="hp-android" aria-hidden="true" />
                下载 Android 版
              </button>
              <!-- official-button-trailing-icon：本按钮的图标在文字【之后】，而其余 .official-button 都是图标在前。
                   老内核没有 flex gap 时要用 margin 复现间距，CSS 区分不出两种结构，所以由模板标出方向（#38）。 -->
              <a class="official-button official-button-ghost official-button-lg official-button-trailing-icon" href="#how-it-works">
                了解如何使用
                <Icon icon="lucide:arrow-right" aria-hidden="true" />
              </a>
            </div>
            <!-- 版本号 / 大小来自只读接口 /public/pkg/info（与 /pkg/download 同一判据选中的那个包，不计下载数），
                 拿到哪项显示哪项；系统要求是确定为真的静态事实（App minSdk = 24）。 -->
            <ul class="official-download-facts" :class="{ 'official-download-facts-live': downloadInfoFacts.length > 0 }" aria-label="下载信息">
              <li v-for="fact in downloadInfoFacts" :key="fact.key" :data-download-fact="fact.key">{{ fact.text }}</li>
              <li>Android 7.0 及以上</li>
            </ul>
          </div>

          <!-- 产品界面示意：照蜜罐 App「应用」桌面页用 HTML/CSS 重画（v5 设计稿）。
               机身里：左上品牌标、右上「+」、4 列应用格子（含下载进度环、角标、虚线「安装新应用」）、
               底部「应用 / 我的」两个标签；右侧两张浮卡取自 App 的邀请安装页与输入邀请码页。
               不是截图，也不含可交互控件。应用格子是分类图标 + 分类名，不画任何真实应用或品牌。
               ≤760px 时两张浮卡收起，只留居中的机身，避免横向溢出。 -->
          <div class="official-product-stage" aria-label="在蜜罐中安装好友分享的应用">
            <div class="official-stage-backdrop" aria-hidden="true"></div>
            <div class="official-phone" aria-hidden="true">
              <div class="official-phone-screen">
                <div class="official-phone-bar">
                  <span>9:41</span>
                  <span class="official-phone-island"></span>
                  <span class="official-phone-battery"><i></i></span>
                </div>
                <div class="official-phone-brand"><img :src="brandMark" alt="" width="22" height="25" /><span>蜜罐</span><Icon class="official-phone-add" icon="lucide:plus" /></div>
                <ul class="official-phone-grid">
                  <li><span class="official-app-tile official-app-tile-orange"><Icon icon="hp-video" /></span><em>视频</em></li>
                  <li><span class="official-app-tile official-app-tile-blue"><Icon icon="hp-book" /><i class="official-app-badge"><Icon icon="hp-download-badge" /></i></span><em>阅读</em></li>
                  <li><span class="official-app-tile official-app-tile-rose"><Icon icon="hp-music" /></span><em>音乐</em></li>
                  <li><span class="official-app-tile official-app-tile-violet"><Icon icon="hp-game" /></span><em>游戏</em></li>
                  <li><span class="official-app-tile official-app-tile-pink"><Icon icon="hp-image" /></span><em>相册</em></li>
                  <li><span class="official-app-tile official-app-tile-green"><Icon icon="hp-note" /></span><em>笔记</em></li>
                  <li class="official-phone-grid-loading">
                    <span class="official-app-tile official-app-tile-sky">
                      <Icon icon="hp-folder" />
                      <i class="official-app-progress">
                        <svg viewBox="0 0 36 36" width="28" height="28"><circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3" /><circle cx="18" cy="18" r="15" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-dasharray="94.2" stroke-dashoffset="51.8" transform="rotate(-90 18 18)" /></svg>
                      </i>
                    </span>
                    <em>下载中</em>
                  </li>
                  <li class="official-phone-grid-add"><span><Icon icon="lucide:plus" /></span><em>安装新应用</em></li>
                </ul>
                <div class="official-phone-tabbar">
                  <span class="official-phone-tab-active"><Icon icon="hp-tab-apps" />应用</span>
                  <span><Icon icon="hp-tab-me" />我的</span>
                </div>
                <span class="official-phone-home"></span>
              </div>
            </div>

            <div class="official-stage-cards" aria-hidden="true">
              <!-- App 邀请安装页：进度条 + 四步 -->
              <div class="official-progress-card">
                <div class="official-progress-card-head">
                  <span class="official-app-tile official-app-tile-sky official-app-tile-sm"><Icon icon="hp-folder" /></span>
                  <div><b>正在下载应用</b><small>文件 · 版本 1.0.6</small></div>
                </div>
                <div class="official-progress-card-meter">
                  <div class="official-progress-card-nums"><b>45%</b><span>12.3MB/27.4MB</span></div>
                  <div class="official-progress-card-track"><i></i></div>
                </div>
                <ol class="official-progress-card-steps">
                  <li class="official-step-done"><span><Icon icon="lucide:check" /></span>解析邀请码</li>
                  <li class="official-step-current"><span><Icon icon="lucide:loader-circle" /></span>下载应用</li>
                  <li><span></span>校验安装包</li>
                  <li><span></span>安装到蜜罐</li>
                </ol>
              </div>
              <!-- App 输入邀请码页：输入框右侧是扫码入口，按钮文案与 App 一致 -->
              <div class="official-invite-card">
                <b>输入邀请码</b>
                <div class="official-invite-card-field">
                  <span>请输入 13 位邀请码或自定义邀请码</span>
                  <Icon icon="hp-scan" />
                </div>
                <span class="official-invite-card-button">识别并安装</span>
              </div>
            </div>
          </div>
        </div>

        <ul class="official-shell official-hero-stats" aria-label="产品特点">
          <li><strong>Android 7.0+</strong><span>覆盖主流机型</span></li>
          <li><strong>无需 Root</strong><span>不改动系统</span></li>
          <li><strong>独立空间</strong><span>应用互相隔离</span></li>
          <li><strong>加密传输</strong><span>请求签名防篡改</span></li>
        </ul>
      </template>

      <!-- 好友分享页（v5 Share 设计稿）：带邀请码打开时替换首屏，下面仍接官网各区块 -->
      <div v-else class="official-shell official-share">
        <div class="official-hero-copy">
          <template v-if="state.kind === 'loading'">
            <p class="official-eyebrow official-eyebrow-muted"><Icon icon="user-plus" aria-hidden="true" />正在打开好友分享</p>
            <section class="official-share-card official-share-card-plain">
              <h1>正在查找应用</h1>
              <p class="official-lead">马上就好，请稍候。</p>
              <div class="official-loading-line"><span></span></div>
            </section>
          </template>

          <template v-else-if="state.kind === 'ready'">
            <p class="official-eyebrow official-eyebrow-muted"><Icon icon="user-plus" aria-hidden="true" />你的好友邀请你安装</p>
            <section class="official-share-card" aria-labelledby="shared-app-name">
              <div class="shared-app-lockup">
                <img v-if="state.app.icon && !imageFailed" :src="state.app.icon" :alt="`${state.app.appName}图标`" @error="imageFailed = true" />
                <span v-else class="shared-app-fallback" aria-hidden="true">应</span>
                <div>
                  <h1 id="shared-app-name">{{ state.app.appName }}</h1>
                  <p class="official-lead">{{ state.app.slogan || '打开蜜罐，查看应用信息后即可安装。' }}</p>
                </div>
              </div>
              <!-- 被分享应用自己的信息，不混入蜜罐安装包信息（/pkg/info）。
                   解析接口不返回包大小与检测结果，所以不写具体数值，只陈述确定为真的流程事实。 -->
              <dl class="official-share-facts" aria-label="分享信息">
                <div><dt>版本</dt><dd>{{ state.app.versionName || '当前版本' }}</dd></div>
                <div><dt>来源</dt><dd>好友分享</dd></div>
                <div><dt>安全检测</dt><dd class="official-share-safe"><Icon icon="lucide:check" aria-hidden="true" />安装前校验</dd></div>
              </dl>
              <div class="official-share-body">
                <div class="official-invite-code">
                  <span>邀请码</span>
                  <code ref="inviteCodeElement">{{ state.inviteCode }}</code>
                  <button id="copy-invite" type="button" aria-label="复制邀请码" data-action="copy-invite" @click="handleCopyInvite">
                    <Icon icon="lucide:copy" aria-hidden="true" />
                    复制
                  </button>
                </div>
                <div class="official-actions official-actions-block">
                  <!-- data-launch-url 暴露本次实际会用的拉起地址(Chromium 为 intent://,其余为裸 scheme),便于自动化断言。
                       点击时同时复制邀请码（toast 回执）；未装蜜罐时弹中性下载引导。 -->
                  <button id="open-honeypot" class="official-button official-button-primary official-button-xl" type="button" data-action="open-honeypot" :data-invite-scheme="inviteSchemeFor(state.inviteCode)" :data-launch-url="launchUrlFor(state.inviteCode)" @click="handleOpenHoneypot">在蜜罐中打开</button>
                </div>
                <p class="official-share-download">还没有安装蜜罐？<button id="share-download" class="official-link-button" type="button" data-action="download-honeypot" @click="handleDirectDownload">下载 Android 版</button></p>
              </div>
            </section>
            <p v-if="customShareText" id="custom-share-text" class="official-custom-share-text">{{ customShareText }}</p>

            <div class="official-share-aside">
              <section class="official-share-panel official-share-steps">
                <h2>安装步骤</h2>
                <ol>
                  <li><span>1</span>点「在蜜罐中打开」，系统询问时选择蜜罐</li>
                  <li><span>2</span>蜜罐识别邀请码，完成安全检测</li>
                  <li><span>3</span>安装完成，可添加到手机桌面</li>
                </ol>
              </section>
              <!-- 二维码只在桌面宽度（≥980px）生成；手机上整块收起 -->
              <section class="official-share-panel official-qr-panel">
                <QrCode :code="state.inviteCode" label="扫码在手机上打开这个分享" />
                <div><strong>在电脑上打开的？</strong><span>用手机扫码，在手机上继续安装。</span></div>
              </section>
            </div>

            <div v-if="isCustomInvite" id="custom-invite-hint" class="official-custom-invite-hint" role="note">
              <Icon icon="lucide:info" aria-hidden="true" />
              <div>
                <p>旧版蜜罐可能识别不了邀请码 <code ref="customHintInviteCodeElement">{{ inviteCode }}</code>。打不开时请先升级蜜罐，或复制邀请码后在蜜罐中手动输入。</p>
                <div class="official-custom-invite-hint-actions">
                  <button id="custom-invite-copy" type="button" @click="handleCopyCustomHintInvite">复制邀请码</button>
                  <button id="custom-invite-upgrade" type="button" @click="handleDirectDownload">升级蜜罐</button>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <p class="official-eyebrow official-eyebrow-muted"><Icon icon="user-plus" aria-hidden="true" />好友分享</p>
            <section class="official-share-card official-share-card-plain">
              <h1>{{ failureTitle }}</h1>
              <p class="official-lead">{{ failureLead }}</p>
              <div class="official-actions">
                <!-- 解析失败（error）时仍把「在蜜罐中打开」放在主位（#57）：已装蜜罐的用户不需要
                     官网先解析出应用信息，App 会自己解析邀请码。「再试一次」退为次要。 -->
                <button v-if="state.kind === 'error'" id="open-honeypot" class="official-button official-button-primary" type="button" data-action="open-honeypot" :data-invite-scheme="inviteSchemeFor(state.inviteCode)" :data-launch-url="launchUrlFor(state.inviteCode)" @click="handleOpenHoneypot">在蜜罐中打开</button>
                <button v-if="state.kind === 'error'" id="retry-resolve" class="official-button official-button-secondary" type="button" data-action="retry-invite" @click="retryResolve">
                  <Icon icon="lucide:refresh-cw" aria-hidden="true" />
                  再试一次
                </button>
                <a v-else class="official-button official-button-secondary" href="/">返回首页</a>
              </div>
            </section>
            <div v-if="isCustomInvite" id="custom-invite-hint" class="official-custom-invite-hint" role="note">
              <Icon icon="lucide:info" aria-hidden="true" />
              <div>
                <p>旧版蜜罐可能识别不了邀请码 <code ref="customHintInviteCodeElement">{{ inviteCode }}</code>。打不开时请先升级蜜罐，或复制邀请码后在蜜罐中手动输入。</p>
                <div class="official-custom-invite-hint-actions">
                  <button id="custom-invite-copy" type="button" @click="handleCopyCustomHintInvite">复制邀请码</button>
                  <button id="custom-invite-upgrade" type="button" @click="handleDirectDownload">升级蜜罐</button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </section>

    <section id="features" class="official-section official-section-features">
      <div class="official-shell">
        <div class="official-section-head official-reveal">
          <p class="official-section-kicker">产品能力</p>
          <h2>为「分享应用」这件事，<br />重新设计每一步。</h2>
        </div>
        <div class="official-feature-grid official-reveal">
          <article v-for="feature in features" :key="feature.title">
            <span class="official-feature-icon"><Icon :icon="feature.icon" aria-hidden="true" /></span>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <section id="how-it-works" class="official-section official-section-flow">
      <div class="official-shell">
        <div class="official-section-head official-section-head-split official-reveal">
          <div>
            <p class="official-section-kicker">使用方法</p>
            <h2>三步，不到一分钟。</h2>
          </div>
          <p>不需要找安装包，也不需要 Root。好友给你一个邀请码就够了。</p>
        </div>
        <ol class="official-flow-visual official-reveal">
          <li class="official-flow-node">
            <span class="official-flow-marker">01</span>
            <div class="official-flow-thumb" aria-hidden="true">
              <span class="official-thumb-button">下载 Android 版</span>
            </div>
            <div class="official-flow-text"><strong>下载蜜罐</strong><small>从官网下载安装包，按系统提示完成安装。</small></div>
          </li>
          <li class="official-flow-node">
            <span class="official-flow-marker">02</span>
            <div class="official-flow-thumb official-flow-thumb-padded" aria-hidden="true">
              <span class="official-thumb-field"><span>demo-2026</span><Icon icon="hp-scan" /></span>
            </div>
            <div class="official-flow-text"><strong>输入邀请码</strong><small>手动输入、复制分享文案或打开分享链接都能识别。</small></div>
          </li>
          <li class="official-flow-node official-flow-node-done">
            <span class="official-flow-marker">03</span>
            <div class="official-flow-thumb official-flow-thumb-padded official-flow-thumb-stack" aria-hidden="true">
              <div class="official-thumb-done"><span class="official-app-tile official-app-tile-orange official-app-tile-sm"><Icon icon="hp-video" /></span><b>安装完成</b></div>
              <div class="official-thumb-buttons"><span>添加到桌面</span><span>打开应用</span></div>
            </div>
            <div class="official-flow-text"><strong>打开应用</strong><small>检测通过后自动安装，可以添加到手机桌面。</small></div>
          </li>
        </ol>
      </div>
    </section>

    <section id="security" class="official-section official-section-security">
      <div class="official-shell official-security-layout official-reveal">
        <div class="official-security-copy">
          <p class="official-section-kicker">安全与隐私</p>
          <h2>安全不是选项，<br />是默认设置。</h2>
          <p>从传输、安装到运行，每一层都有对应的保护。</p>
        </div>
        <ul class="official-security-list">
          <li><strong>传输全程加密</strong><p>与服务端的通信全程加密，请求签名防篡改、防重放。</p></li>
          <li><strong>安装包校验</strong><p>安装前校验来源与完整性，被篡改的包不会安装。</p></li>
          <li><strong>凭据不出账号页</strong><p>密码与验证码只在聚点账号页面输入，官网拿不到。</p></li>
          <li><strong>应用相互隔离</strong><p>应用之间互不读取数据，移除时一并清理。</p></li>
        </ul>
      </div>
    </section>

    <section id="download" class="official-cta-section" aria-labelledby="official-cta-title">
      <div class="official-shell">
        <div class="official-cta official-reveal">
          <div class="official-cta-copy">
            <h2 id="official-cta-title">准备好了吗？</h2>
            <p>下载蜜罐，打开好友分享给你的第一个应用。</p>
            <div class="official-cta-actions">
              <button id="cta-download" class="official-button official-button-primary official-button-lg" type="button" data-action="download-honeypot" @click="handleDirectDownload">下载 Android 版</button>
              <a class="official-button official-button-secondary official-button-lg" href="/me">登录查看我的应用</a>
            </div>
            <p v-if="downloadInfoSummaryParts.length" id="cta-download-info" class="official-cta-meta"><template v-for="(part, index) in downloadInfoSummaryParts" :key="part">{{ index ? ' · ' : '' }}<span>{{ part }}</span></template></p>
          </div>
          <!-- 二维码只在桌面宽度（≥980px）生成；手机上整块收起 -->
          <div class="official-qr-panel official-cta-qr">
            <QrCode :code="openableInviteCode" :label="openableInviteCode ? '扫码在手机上打开这个分享' : '扫码在手机上打开蜜罐官网'" />
            <div><strong>{{ openableInviteCode ? '扫码打开' : '扫码下载' }}</strong><span>用手机浏览器扫描</span></div>
          </div>
        </div>
      </div>
    </section>

    <section id="faq" class="official-section official-section-faq">
      <div class="official-shell official-faq-layout official-reveal">
        <div class="official-faq-aside">
          <h2>常见问题</h2>
          <p>没有找到答案？在蜜罐 App「我的 → 用户反馈」联系我们。</p>
          <a class="official-help-link" href="/help/desktop-shortcut/">各品牌手机开启桌面快捷方式权限<Icon icon="lucide:arrow-right" aria-hidden="true" /></a>
        </div>
        <div class="official-faq-list">
          <details open>
            <summary>邀请码在哪里获取？<Icon icon="lucide:chevron-down" /></summary>
            <p>邀请码由分享应用的好友提供，可能是 13 位标准码，也可能是好记的自定义码，例如 demo-2026。</p>
          </details>
          <details>
            <summary>需要注册账号吗？<Icon icon="lucide:chevron-down" /></summary>
            <p>安装应用不需要登录。登录聚点账号后，可以在官网查看已安装的应用。</p>
          </details>
          <details>
            <summary>支持哪些手机？<Icon icon="lucide:chevron-down" /></summary>
            <p>目前提供 Android 版，支持 Android 7.0 及以上。iPhone / iPad 无法安装，在苹果设备上收到分享时，可以复制链接到安卓手机上打开。</p>
          </details>
          <details>
            <summary>安装的应用安全吗？<Icon icon="lucide:chevron-down" /></summary>
            <p>每个应用安装前都会校验来源与完整性，并运行在独立空间中。</p>
          </details>
          <details>
            <summary>在微信或 QQ 里点了没反应？<Icon icon="lucide:chevron-down" /></summary>
            <p>微信、QQ 的内置浏览器会限制下载和唤起应用。请点击右上角 ···，选择「在浏览器中打开」，再继续操作。</p>
          </details>
          <details>
            <summary>已经装了蜜罐，却没有被唤起？<Icon icon="lucide:chevron-down" /></summary>
            <p>部分手机会先弹出「是否打开」的系统确认，请选择允许。如果仍未唤起，可以在页面提示中点击下载，重新安装蜜罐后再打开分享链接。</p>
          </details>
          <details>
            <summary>添加桌面快捷方式失败？<Icon icon="lucide:chevron-down" /></summary>
            <p>部分手机默认关闭了「创建桌面快捷方式」权限。<a href="/help/desktop-shortcut/">查看各品牌手机的开启方法</a>，开启后回到蜜罐重新添加即可。</p>
          </details>
        </div>
      </div>
    </section>
  </main>

  <footer class="official-footer">
    <div class="official-shell official-footer-main">
      <div class="official-footer-about">
        <div class="official-brand"><img :src="brandMark" alt="" width="21" height="24" /><span>蜜罐</span></div>
        <p>好友分享的应用，一码就能装好。</p>
      </div>
      <div class="official-footer-cols">
        <nav class="official-footer-col" aria-label="产品">
          <h3>产品</h3>
          <a href="#features">产品能力</a>
          <a href="#how-it-works">使用方法</a>
          <a href="#download">下载</a>
        </nav>
        <nav class="official-footer-col" aria-label="支持">
          <h3>支持</h3>
          <a href="#faq">帮助中心</a>
          <a href="#security">安全说明</a>
          <a href="/help/desktop-shortcut/">桌面快捷方式帮助</a>
        </nav>
        <nav class="official-footer-col" aria-label="账号">
          <h3>账号</h3>
          <a href="/me">我的应用</a>
        </nav>
      </div>
    </div>
    <div class="official-shell official-footer-bottom">
      <p>© 2026 蜜罐</p>
      <p>仅提供 Android 版本</p>
    </div>
  </footer>

  <!--
    未检测到蜜罐时的提示。下载由隐藏 iframe 承担，页面不跳走，弹窗一直留在用户眼前——
    自动下载被浏览器拦掉时，用户可直接点弹窗里的主按钮重试（右上角 X 仍可关闭）。
  -->
  <dialog id="official-download-dialog" ref="downloadDialog" class="official-dialog">
    <button class="official-dialog-close" type="button" aria-label="关闭" @click="closeDialog(downloadDialog)"><Icon icon="lucide:x" /></button>
    <!--
      图标与文案的主语都必须是【蜜罐】(#31)：这里检测的是蜜罐有没有安装、下载的也是蜜罐，
      被分享的那个应用既不是检测对象也不是下载对象。
    -->
    <img :src="honeypotIcon" alt="蜜罐图标" />
    <!--
      看门狗超时那条路（timeout 档）不得说「未检测到」——它只是「到点了页面还在」，
      已装蜜罐的用户在系统确认框上多停留一会儿就会被它冤枉（#57 vivo 真机）。
    -->
    <h2>{{ downloadDialogVariant === 'timeout' ? '未唤起蜜罐？' : '未检测到蜜罐' }}</h2>
    <p id="download-copy-status">
      <template v-if="downloadDialogVariant === 'timeout'">若未唤起蜜罐，请点击此处下载。</template>
      <template v-else>本机未检测到蜜罐{{ launchFallbackAppLabel ? `（${launchFallbackAppLabel}需通过蜜罐安装）` : '' }}，点下面的按钮下载蜜罐。</template>
    </p>
    <div class="official-dialog-invite-code">
      <code ref="dialogInviteCodeElement">{{ inviteCode }}</code>
      <button id="retry-invite-copy" type="button" @click="retryCopyInvite"><Icon icon="lucide:copy" aria-hidden="true" /> 复制邀请码</button>
    </div>
    <!-- 当前安装包信息（与首屏、收尾横幅同一份数据）；接口没给就整行不出 -->
    <p v-if="downloadInfoSummaryParts.length" id="dialog-download-info" class="official-dialog-meta"><template v-for="(part, index) in downloadInfoSummaryParts" :key="part">{{ index ? ' · ' : '' }}<span>{{ part }}</span></template></p>
    <button id="manual-download" class="official-button official-button-primary" type="button" @click="handleDirectDownload">{{ downloadDialogVariant === 'timeout' ? '点击此处下载' : '没有反应？点我下载' }}</button>
  </dialog>

  <iframe ref="downloadFrame" class="official-download-frame" title="蜜罐下载" aria-hidden="true" tabindex="-1"></iframe>

  <dialog id="restricted-browser-dialog" ref="restrictedDialog" class="official-dialog">
    <button class="official-dialog-close" type="button" aria-label="关闭" @click="closeDialog(restrictedDialog)"><Icon icon="lucide:x" /></button>
    <div class="official-browser-icon"><Icon icon="lucide:compass" /></div>
    <h2>请在浏览器中打开</h2>
    <p>点击右上角菜单，选择“在浏览器中打开”，再继续下载或打开蜜罐。</p>
    <button class="official-button official-button-secondary" type="button" @click="closeDialog(restrictedDialog)">我知道了</button>
  </dialog>

  <!-- popover=manual：只由脚本控制开合，用于把提示抬进 top layer 盖在模态弹窗之上 -->
  <div id="official-toast" ref="toastElement" popover="manual" class="official-toast" :class="{ 'official-toast-visible': toastVisible }" role="status">{{ toastMessage }}</div>
  </template>
</template>
